import moment from "moment";
import { apiDebtStatusToSlug } from "../DebtStatusBadge";
import {
  DEBT_DETAIL_DISPLAY_STATUS_PRIORITY,
  DEBT_STATUS,
} from "./DebtCollectionSectionConstants";

/**
 * Lowercases and trims a status string for comparisons.
 * @param {string|number|null|undefined} status - Raw status.
 * @returns {string} Normalized slug fragment or empty string.
 */
export function normalizeStatus(status) {
  if (status == null || status === "") return "";
  return String(status)
    .trim()
    .toLowerCase();
}

/**
 * Formats a numeric amount as GBP for debt detail tiles.
 * @param {string|number|null|undefined} value - Raw monetary value.
 * @returns {string} Formatted currency or em dash when missing or invalid.
 */
export function formatMoney(value) {
  if (value == null || value === "") return "—";
  const n = Number(value);
  if (Number.isNaN(n)) return "—";
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
    minimumFractionDigits: 2,
  }).format(n);
}

/**
 * Extracts an array of debt rows from varied API response envelopes.
 * @param {import("axios").AxiosResponse|{ data?: unknown }} res - Axios response or similar.
 * @returns {object[]} Debt row objects (possibly empty).
 */
export function extractDebtRowsFromApiResponse(res) {
  const payload = res?.data?.data ?? res?.data;
  if (payload == null) return [];
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload.data)) return payload.data;
  if (typeof payload === "object" && payload.id != null) return [payload];
  return [];
}

/**
 * Builds a display string for a user reference on franchise debt detail.
 * @param {string|object|null|undefined} u - User object or string id.
 * @returns {string} Display name or empty string.
 */
export function formatDebtUserRef(u) {
  if (u == null || u === "") return "";
  if (typeof u === "string") return u;
  if (typeof u === "object") {
    const fn = u.first_name || u.firstName;
    const ln = u.last_name || u.lastName;
    const joined = [fn, ln].filter(Boolean).join(" ");
    if (joined) return joined;
    return u.name || u.email || "";
  }
  return String(u);
}

/**
 * Chooses the best "status owner" label for audit display from the record and slug.
 * @param {object|null|undefined} record - Normalized debt record.
 * @param {string} statusSlug - Status slug from {@link apiDebtStatusToSlug}.
 * @returns {string} Display string or em dash.
 */
export function getDebtStatusOwnerDisplay(record, statusSlug) {
  if (!record) return "—";
  if (statusSlug === "closed") {
    return (
      record.closed_by_display || formatDebtUserRef(record.closed_by) || "—"
    );
  }
  const byUpdated =
    record.updated_by_display || formatDebtUserRef(record.updated_by);
  if (byUpdated) return byUpdated;
  return (
    record.created_by_display || formatDebtUserRef(record.created_by) || "—"
  );
}

/**
 * Normalizes a raw API debt row with derived slug, dates, and display fields.
 * @param {object|null|undefined} raw - Raw row from the API.
 * @returns {object|null} Enriched record or null when input is invalid.
 */
export function normalizeApiDebtRecord(raw) {
  if (!raw || typeof raw !== "object") return null;
  const startRaw = raw.started_at ?? raw.start_date ?? null;
  const closureRaw = raw.closed_at ?? raw.closure_date ?? null;
  const slug = apiDebtStatusToSlug(raw.status);
  return {
    ...raw,
    _statusSlug: slug,
    start_date: startRaw,
    closure_date: closureRaw,
    closure_remark: raw.closure_remark ?? raw.closed_reason ?? null,
    closed_by_display: formatDebtUserRef(raw.closed_by),
    created_by_display: formatDebtUserRef(raw.created_by),
    updated_by_display: formatDebtUserRef(raw.updated_by),
  };
}

/**
 * Coerces API status (number, numeric string, or slug) to the numeric enum code.
 * @param {string|number|null|undefined} status - Raw status from API.
 * @returns {number|null} `DEBT_STATUS` code or null when unknown.
 */
export function coerceFranchiseDebtStatusCode(status) {
  if (status == null || status === "") return null;
  if (typeof status === "number" && Number.isFinite(status)) return status;
  const s = String(status).trim();
  if (/^\d+$/.test(s)) return Number(s);
  const slug = normalizeStatus(status);
  const fromSlug = {
    pending: DEBT_STATUS.Pending,
    ongoing: DEBT_STATUS.Ongoing,
    paused: DEBT_STATUS.Paused,
    completed: DEBT_STATUS.Completed,
    closed: DEBT_STATUS.Closed,
  };
  return fromSlug[slug] ?? null;
}

/** Timestamp used to pick the newest row among same-status duplicates. */
function parseDebtRecencyMsForSelection(raw) {
  if (!raw || typeof raw !== "object") return 0;
  const created = moment(raw.created_at);
  const started = moment(raw.started_at ?? raw.start_date);
  const c = created.isValid() ? created.valueOf() : 0;
  const s = started.isValid() ? started.valueOf() : 0;
  const fromCreateOrStart = Math.max(c, s);
  if (fromCreateOrStart > 0) return fromCreateOrStart;
  const u = moment(raw.updated_at);
  return u.isValid() ? u.valueOf() : 0;
}

/** Picks the most recently updated/created row from a non-empty list. */
function pickLatestDebtRowByRecency(rows) {
  if (!rows?.length) return null;
  let best = rows[0];
  let bestT = parseDebtRecencyMsForSelection(best);
  for (let i = 1; i < rows.length; i += 1) {
    const r = rows[i];
    const t = parseDebtRecencyMsForSelection(r);
    if (t > bestT) {
      best = r;
      bestT = t;
    }
  }
  return best;
}

/**
 * Chooses the single debt row to show on franchise details using status priority and recency.
 * @param {object[]|null|undefined} rows - Candidate rows from the API.
 * @returns {object|null} Selected row or null.
 */
export function selectActiveDebtConfigurationRow(rows) {
  const list = (rows || []).filter((r) => r && typeof r === "object");
  if (!list.length) return null;

  for (const code of DEBT_DETAIL_DISPLAY_STATUS_PRIORITY) {
    const subset = list.filter(
      (r) => coerceFranchiseDebtStatusCode(r.status) === code,
    );
    if (subset.length) return pickLatestDebtRowByRecency(subset);
  }
  return null;
}

/**
 * Parses a franchise debt fetch response into one normalized record for the UI.
 * @param {import("axios").AxiosResponse|{ data?: unknown }} res - Response from `getFranchiseDebt`.
 * @returns {object|null} Normalized record or null when none.
 */
export function parseDebtRecordFromFetchResponse(res) {
  const rows = extractDebtRowsFromApiResponse(res);
  if (!rows.length) return null;
  const chosen = selectActiveDebtConfigurationRow(rows);
  return chosen ? normalizeApiDebtRecord(chosen) : null;
}
